import React from "react";
import SEO from "../components/SEO";
import TaxiCard from "../components/TaxiCard";
import "../styles/taxi.css";

export default function Taxi() {
  /* =====================================================
     TAXI SERVICES
  ====================================================== */

  const taxiServices = [
    {
      name: "Swift Dzire Taxi",
      type: "Sedan • 5 Seater • AC",
      price: "₹12/km",
      image:
        "https://stimg.cardekho.com/images/carexteriorimages/630x420/Maruti/Swift-Dzire/7664/1675859508504/front-left-side-47.jpg?imwidth=420&impolicy=resize",
    },
    {
      name: "Toyota Innova Taxi",
      type: "SUV • 7 Seater • AC",
      price: "₹18/km",
      image:
        "https://www.team-bhp.com/sites/default/files/styles/amp_high_res/public/inn1.jpeg",
    },
    {
      name: "Maruti Ertiga Taxi",
      type: "SUV • 7 Seater • AC",
      price: "₹16/km",
      image:
        "https://imgd.aeplcdn.com/1920x1080/n/cw/ec/115777/ertiga-exterior-left-side-view.jpeg?isig=0&q=80",
    },
    {
      name: "Udaipur Airport Pickup & Drop",
      type: "Fixed Price Service • Swift Dzire / Maruti Ertiga",
      price: "₹800–₹1,800",
      image:
        "https://5.imimg.com/data5/SELLER/Default/2024/2/388918827/HY/SC/PM/103718232/mumbai-airport-pickup-drop.png",
    },
  ];

  /* =====================================================
     BREADCRUMBS
  ====================================================== */

  const breadcrumbs = [
    {
      name: "Home",
      url: "https://vkbikeonrent.in/",
    },
    {
      name: "Taxi & Cab Services",
      url: "https://vkbikeonrent.in/taxi",
    },
  ];

  /* =====================================================
     FAQ CONTENT
  ====================================================== */

  const faqs = [
    {
      question: "Does VK Bike On Rent provide taxi service in Udaipur?",
      answer:
        "Yes. VK Bike On Rent provides taxi and cab service options in Udaipur, including sedan and SUV options subject to availability.",
    },
    {
      question: "Which taxi vehicles are available in Udaipur?",
      answer:
        "The currently listed options include Swift Dzire, Toyota Innova and Maruti Ertiga, subject to availability.",
    },
    {
      question: "Does VK Bike On Rent provide Udaipur airport pickup and drop?",
      answer:
        "Yes. Airport pickup and drop is listed as a service. The current listed fixed-price range is ₹800–₹1,800. Contact VK Bike On Rent to confirm the applicable price and vehicle before booking.",
    },
    {
      question: "How much does a taxi cost in Udaipur?",
      answer:
        "The currently listed taxi rates start from ₹12/km for a Swift Dzire, ₹16/km for a Maruti Ertiga and ₹18/km for a Toyota Innova. Prices and availability should be confirmed before booking.",
    },
    {
      question: "How can I book a taxi in Udaipur?",
      answer:
        "Contact VK Bike On Rent through WhatsApp to check vehicle availability, current pricing and booking details.",
    },
  ];

  /* =====================================================
     FAQ SCHEMA
  ====================================================== */

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      {/* =====================================================
          PAGE SEO
      ====================================================== */}

      <SEO
        title="Taxi & Cab Service in Udaipur | Airport Taxi | VK Bike On Rent"
        description="Taxi and cab services in Udaipur for local travel, sightseeing and airport pickup and drop. Swift Dzire, Ertiga and Innova options available subject to availability."
        canonical="https://vkbikeonrent.in/taxi"
        breadcrumbs={breadcrumbs}
        schema={faqSchema}
      />

      {/* =====================================================
          TAXI PAGE
      ====================================================== */}

      <main className="taxi-section">

        {/* =================================================
            PAGE INTRODUCTION
        ================================================== */}

        <header className="taxi-header">

          <h1 className="taxi-heading">
            Taxi & Cab Services in Udaipur
          </h1>

          <p className="taxi-intro">
            Book a taxi or cab in Udaipur for local travel,
            sightseeing and airport transfers. Choose from
            available sedan and SUV options and contact
            VK Bike On Rent to confirm current availability,
            pricing and booking details.
          </p>

        </header>

        {/* =================================================
            AVAILABLE TAXIS
        ================================================== */}

        <section
          className="taxi-services"
          aria-labelledby="available-taxi-services"
        >

          <h2 id="available-taxi-services">
            Available Taxi Services in Udaipur
          </h2>

          <div className="taxi-grid">

            {taxiServices.map((service) => (
              <TaxiCard
                key={service.name}
                name={service.name}
                type={service.type}
                price={service.price}
                image={service.image}
              />
            ))}

          </div>

        </section>

        {/* =================================================
            LOCAL TAXI INFORMATION
        ================================================== */}

        <section
          className="taxi-seo-content"
          aria-labelledby="udaipur-taxi-information"
        >

          <h2 id="udaipur-taxi-information">
            Taxi Services for Udaipur Travel
          </h2>

          <p>
            Looking for a taxi in Udaipur? VK Bike On Rent
            provides taxi options for visitors who need
            convenient transportation around the city.
            Available vehicles include sedans and SUVs,
            subject to availability.
          </p>

          <p>
            Taxi services can be useful for local sightseeing,
            airport transfers and other travel requirements
            around Udaipur. Contact us before your journey
            to confirm the vehicle, pricing and availability.
          </p>

          {/* =================================================
              AIRPORT
          ================================================== */}

          <h2>
            Udaipur Airport Pickup & Drop
          </h2>

          <p>
            Airport pickup and drop services are available
            with selected vehicles. The currently listed
            fixed-price range is{" "}
            <strong>₹800–₹1,800</strong>.
            Please contact VK Bike On Rent before booking
            to confirm the applicable price, vehicle and
            availability.
          </p>

          {/* =================================================
              TAXI OPTIONS
          ================================================== */}

          <h2>
            Taxi Options Available
          </h2>

          <ul>

            <li>
              <strong>Swift Dzire:</strong>{" "}
              Sedan, 5 seats, AC — listed from ₹12/km
            </li>

            <li>
              <strong>Toyota Innova:</strong>{" "}
              SUV, 7 seats, AC — listed from ₹18/km
            </li>

            <li>
              <strong>Maruti Ertiga:</strong>{" "}
              SUV, 7 seats, AC — listed from ₹16/km
            </li>

            <li>
              <strong>Airport pickup & drop:</strong>{" "}
              Listed fixed-price options from ₹800–₹1,800
            </li>

          </ul>

        </section>

        {/* =================================================
            UDAIPUR TRAVEL
        ================================================== */}

        <section
          className="taxi-local-section"
          aria-labelledby="udaipur-sightseeing-title"
        >

          <h2 id="udaipur-sightseeing-title">
            Taxi for Local Sightseeing in Udaipur
          </h2>

          <p>
            A taxi can be a convenient option for visitors
            exploring Udaipur's major attractions and
            surrounding areas. Depending on your requirements,
            you can enquire about available sedan or SUV
            options for your trip.
          </p>

          <p>
            Popular places visitors explore in and around
            Udaipur include City Palace, Lake Pichola,
            Jagdish Temple, Fateh Sagar Lake, Gangaur Ghat
            and Saheliyon Ki Bari.
          </p>

        </section>

        {/* =================================================
            AEO / FAQ
        ================================================== */}

        <section
          className="taxi-faq"
          aria-labelledby="taxi-faq-title"
        >

          <h2 id="taxi-faq-title">
            Taxi & Cab Service in Udaipur – FAQs
          </h2>

          {faqs.map((faq) => (
            <article
              className="taxi-faq-item"
              key={faq.question}
            >

              <h3>
                {faq.question}
              </h3>

              <p>
                {faq.answer}
              </p>

            </article>
          ))}

        </section>

        {/* =================================================
            FINAL CTA
        ================================================== */}

        <section
          className="taxi-booking-cta"
          aria-labelledby="taxi-booking-title"
        >

          <h2 id="taxi-booking-title">
            Check Taxi Availability in Udaipur
          </h2>

          <p>
            Need a taxi for sightseeing, airport transfer
            or local travel? Contact VK Bike On Rent on
            WhatsApp to check current vehicle availability,
            pricing and booking details.
          </p>

          <a
            href="https://wa.me/919983765215?text=Hi%20VK%20Bike%20On%20Rent%2C%20I%20want%20to%20enquire%20about%20taxi%20service%20in%20Udaipur."
            target="_blank"
            rel="noopener noreferrer"
            className="taxi-booking-btn"
            aria-label="Check taxi availability in Udaipur on WhatsApp"
          >
            📲 Check Availability on WhatsApp
          </a>

        </section>

      </main>
    </>
  );
}